import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { redrawServicesCapabilitiesConnectors } from './services-capabilities-connectors.js';

gsap.registerPlugin(ScrollTrigger);

const DESKTOP_MIN = 1100;
const HUB_ORIGIN = '50% 50%';

/**
 * @param {HTMLElement} diagram
 * @returns {gsap.core.Timeline | null}
 */
const buildConnectorLineTimeline = (diagram) => {
	const paths = diagram.querySelectorAll('.js-services-cap-line');
	const dots = diagram.querySelectorAll('.js-services-cap-dot');

	if (!paths.length) {
		return null;
	}

	const lineTimeline = gsap.timeline({
		onComplete: () => {
			diagram.dataset.linesAnimated = 'true';
		},
	});

	paths.forEach((path, index) => {
		const length = path.getTotalLength();
		gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
		lineTimeline.to(
			path,
			{ strokeDashoffset: 0, duration: 0.6, ease: 'power2.inOut' },
			index * 0.07,
		);
	});

	if (dots.length) {
		gsap.set(dots, { autoAlpha: 0 });
		lineTimeline.to(
			dots,
			{ autoAlpha: 1, duration: 0.35, stagger: 0.07, ease: 'power2.out' },
			0.22,
		);
	}

	return lineTimeline;
};

/**
 * @param {HTMLElement} diagram
 */
const bindDiagramDesktop = (diagram) => {
	const section = diagram.closest('.section--services-capabilities');
	const trigger = section || diagram;
	const headings = diagram.querySelectorAll('.services-cap-diagram__heading');
	const leftCards = diagram.querySelectorAll(
		'.services-cap-diagram__column--left .services-cap-diagram__card',
	);
	const rightCards = diagram.querySelectorAll(
		'.services-cap-diagram__column--right .services-cap-diagram__card',
	);
	const hub = diagram.querySelector('.services-cap-diagram__hub');
	const hubArt = diagram.querySelector('.services-cap-diagram__hub-art');
	const hubDisc = diagram.querySelector('.js-services-cap-hub');
	const hubLogo = diagram.querySelector('.services-cap-diagram__hub-logo');

	gsap.set(headings, { autoAlpha: 0, y: 24 });
	// Bloom the whole hub art layer so glows/rings keep their CSS centering (no per-layer scale).
	if (hubArt) {
		gsap.set(hubArt, { autoAlpha: 0, scale: 0.72, transformOrigin: HUB_ORIGIN });
	}
	if (hubLogo) {
		gsap.set(hubLogo, { autoAlpha: 0, y: 10 });
	}
	gsap.set(leftCards, { autoAlpha: 0, x: -64, y: 18 });
	gsap.set(rightCards, { autoAlpha: 0, x: 64, y: 18 });
	diagram.dataset.layoutAnimating = 'true';

	const enableHubBreathe = () => {
		if (!hub || hub.classList.contains('services-cap-diagram__hub--breathe')) {
			return;
		}

		gsap.set(hubArt, { clearProps: 'transform' });
		hub.classList.add('services-cap-diagram__hub--breathe');
	};

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger,
			start: 'top 78%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
		onComplete: () => {
			gsap.set(hubLogo, { clearProps: 'transform,opacity,visibility' });
		},
	});

	entrance.to(headings, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.12 }, 0);

	if (hubArt) {
		entrance.to(
			hubArt,
			{
				autoAlpha: 1,
				scale: 1,
				duration: 1,
				ease: 'power2.out',
				onComplete: enableHubBreathe,
			},
			0.12,
		);
	}

	if (hubLogo) {
		entrance.to(hubLogo, { autoAlpha: 1, y: 0, duration: 0.55 }, 0.58);
	}

	entrance
		.to(
			leftCards,
			{ autoAlpha: 1, x: 0, y: 0, duration: 0.68, stagger: 0.09, ease: 'back.out(1.35)' },
			0.38,
		)
		.to(
			rightCards,
			{ autoAlpha: 1, x: 0, y: 0, duration: 0.68, stagger: 0.09, ease: 'back.out(1.35)' },
			0.44,
		)
		.add(() => {
			delete diagram.dataset.layoutAnimating;
			gsap.set([...leftCards, ...rightCards, hubDisc].filter(Boolean), { x: 0, y: 0, scale: 1 });
			redrawServicesCapabilitiesConnectors(diagram);
			return buildConnectorLineTimeline(diagram);
		}, 1.42);

	requestAnimationFrame(() => {
		ScrollTrigger.refresh();
		if (entrance.progress() === 1) {
			enableHubBreathe();
		}
	});

	const animated = [
		...headings,
		hubArt,
		hubLogo,
		...leftCards,
		...rightCards,
	].filter(Boolean);

	return () => {
		hub?.classList.remove('services-cap-diagram__hub--breathe');
		entrance.scrollTrigger?.kill();
		delete diagram.dataset.linesAnimated;
		delete diagram.dataset.layoutAnimating;
		gsap.killTweensOf(animated);
		gsap.set(animated, { clearProps: 'all' });
	};
};

/**
 * @param {HTMLElement} diagram
 */
const bindDiagramReduced = (diagram) => {
	const section = diagram.closest('.section--services-capabilities');
	const trigger = section || diagram;
	const targets = diagram.querySelectorAll(
		'.services-cap-diagram__heading, .services-cap-diagram__card, .services-cap-diagram__hub',
	);

	if (!targets.length) {
		return null;
	}

	gsap.set(targets, { autoAlpha: 0, y: 16 });

	const tween = gsap.to(targets, {
		autoAlpha: 1,
		y: 0,
		duration: 0.55,
		stagger: 0.06,
		ease: 'power2.out',
		scrollTrigger: {
			trigger,
			start: 'top 85%',
			once: true,
		},
	});

	return () => {
		tween.scrollTrigger?.kill();
		gsap.killTweensOf(targets);
		gsap.set(targets, { clearProps: 'all' });
	};
};

export const initServicesCapabilitiesAnimation = () => {
	const diagrams = document.querySelectorAll('.js-services-cap-diagram');
	if (!diagrams.length) {
		return;
	}

	gsap.matchMedia().add(`(prefers-reduced-motion: no-preference) and (min-width: ${DESKTOP_MIN}px)`, () => {
		const cleanups = [];

		diagrams.forEach((diagram) => {
			const cleanup = bindDiagramDesktop(diagram);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});

	gsap.matchMedia().add(
		`(prefers-reduced-motion: no-preference) and (max-width: ${DESKTOP_MIN - 1}px)`,
		() => {
			const cleanups = [];

			diagrams.forEach((diagram) => {
				const cleanup = bindDiagramReduced(diagram);
				if (cleanup) {
					cleanups.push(cleanup);
				}
			});

			return () => cleanups.forEach((fn) => fn());
		},
	);

	gsap.matchMedia().add('(prefers-reduced-motion: reduce)', () => {
		const cleanups = [];

		diagrams.forEach((diagram) => {
			const cleanup = bindDiagramReduced(diagram);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
